import { c as _c } from "react/compiler-runtime";
import { IcoArrowTopRight16, IcoCrosshair16, IcoNetwork16 } from '@onefootprint/icons';
import { select, type ZoomBehavior, zoom, zoomIdentity } from 'd3';
import Link from 'next/link';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
import type { ClusterBlockConfig, ClusterEdge } from './artifact-block-types';
import { CLUSTER_GRAPH_WIDTH, type ClusterGraphNode, type ClusterLayoutEdge, getClusterEntities, isDenseCluster, layoutClusterGraph } from './artifact-cluster-layout';
import InlineText from './inline-text';
type ArtifactClusterBlockProps = {
  config: ClusterBlockConfig;
};
type GraphSelection = {
  kind: 'edge';
  index: number;
} | {
  kind: 'node';
  id: string;
};
const nodePresentation = {
  other: {
    kind: 'Related entity',
    glyph: 'C',
    fill: 'var(--background-color-secondary)',
    stroke: 'var(--border-color-primary)'
  },
  fp_id: {
    kind: 'Footprint person',
    glyph: 'P',
    fill: 'var(--background-color-primary)',
    stroke: 'var(--border-color-accent)'
  },
  fp_bid: {
    kind: 'Footprint business',
    glyph: 'B',
    fill: 'var(--background-color-primary)',
    stroke: 'var(--border-color-accent)'
  }
} satisfies Record<ClusterGraphNode['kind'], {
  kind: string;
  glyph: string;
  fill: string;
  stroke: string;
}>;
const getNodeHref = (node: ClusterGraphNode) => {
  if (node.kind === 'fp_id') return `/users/${node.id}`;
  if (node.kind === 'fp_bid') return `/businesses/${node.id}`;
  return null;
};
const truncate = (value: string, maxLength: number) => value.length <= maxLength ? value : `${value.slice(0, maxLength - 1)}…`;
const getEdgeAriaLabel = (edge: ClusterLayoutEdge) => `Inspect ${edge.attribute} link between ${edge.source.id} and ${edge.target.id}`;
const handleSelectKey = (event: KeyboardEvent<SVGGElement>, selectItem: () => void) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  selectItem();
};
type ClusterGraphProps = {
  edges: ClusterEdge[];
  selection: GraphSelection;
  onSelect: (selection: GraphSelection) => void;
};

// Laid out narrower on a phone so the scaled-down graph keeps nodes large enough to tap and labels to read.
const MOBILE_CLUSTER_GRAPH_WIDTH = 420;
const ClusterGraph = t0 => {
  const $ = _c(30);
  const {
    edges,
    selection,
    onSelect
  } = t0;
  const isMobile = useMobileLayout();
  const graphWidth = isMobile ? MOBILE_CLUSTER_GRAPH_WIDTH : CLUSTER_GRAPH_WIDTH;
  const layout = layoutClusterGraph(edges, graphWidth);
  const dense = isDenseCluster(layout.nodes.length);
  const nodeRadius = dense ? 13 : 17;
  const nodeLabelLength = dense ? 13 : 18;
  const nodeLabelFontSize = dense ? 8 : 9;
  const nodeGlyphFontSize = dense ? 8 : 10;
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const zoomRef = useRef(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [pointerEdgeIndex, setPointerEdgeIndex] = useState(null);
  const [focusedEdgeIndex, setFocusedEdgeIndex] = useState(null);
  const hoveredNeighborhood = new Set();
  const selectedEdge = selection.kind === "edge" ? layout.edges[selection.index] : undefined;
  const previewedEdgeIndex = pointerEdgeIndex ?? focusedEdgeIndex;
  const inspectedEdge = previewedEdgeIndex === null ? selectedEdge : layout.edges[previewedEdgeIndex];
  const selectedEdgeEndpoints = new Set(selectedEdge ? [selectedEdge.source.id, selectedEdge.target.id] : []);
  if (hoveredNodeId) {
    hoveredNeighborhood.add(hoveredNodeId);
    for (const edge of layout.edges) {
      if (edge.source.id === hoveredNodeId) {
        hoveredNeighborhood.add(edge.target.id);
      }
      if (edge.target.id === hoveredNodeId) {
        hoveredNeighborhood.add(edge.source.id);
      }
    }
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const element = svgRef.current;
      if (!element) {
        return;
      }
      const behavior = zoom().scaleExtent([0.55, 2.75]).on("zoom", event => viewportRef.current?.setAttribute("transform", event.transform.toString()));
      zoomRef.current = behavior;
      const svg = select(element);
      svg.call(behavior);
      return () => {
        svg.on(".zoom", null);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== isMobile) {
    t3 = () => {
      const element_0 = svgRef.current;
      zoomRef.current?.filter(event_0 => {
        if (isMobile && event_0.type === "touchstart") {
          return event_0.touches.length > 1;
        }
        return event_0.type === "wheel" ? event_0.ctrlKey || event_0.metaKey : event_0.target === element_0;
      });
    };
    t4 = [isMobile];
    $[2] = isMobile;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const element_1 = svgRef.current;
      const behavior_0 = zoomRef.current;
      if (!element_1 || !behavior_0) {
        return;
      }
      select(element_1).call(behavior_0.transform, zoomIdentity);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const resetViewport = t5;
  const t6 = "relative overflow-hidden rounded-xl border border-tertiary bg-secondary [background-image:radial-gradient(var(--border-color-tertiary)_1px,transparent_1px)] [background-size:16px_16px]";
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <button aria-label="Reset graph view" className="absolute right-3 top-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-default border border-tertiary bg-primary shadow-sm transition-colors hover:bg-hover-control" onClick={resetViewport} type="button"><IcoCrosshair16 color="tertiary" /></button>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = inspectedEdge && <div aria-hidden="true" className="pointer-events-none absolute left-3 top-3 z-10 flex max-w-[calc(100%-5rem)] items-center gap-1.5 rounded-lg border border-tertiary bg-primary/95 px-2.5 py-1.5 text-caption-4 shadow-sm backdrop-blur" data-testid="cluster-connection-preview"><span aria-hidden={true} className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /><span className="min-w-0 truncate text-label-3 text-primary">{inspectedEdge.attribute}</span>{inspectedEdge.value && <><span aria-hidden={true} className="text-tertiary">·</span><span className="min-w-0 truncate text-secondary">{inspectedEdge.value}</span></>}</div>;
  const t9 = `Entity graph with ${layout.nodes.length} nodes and ${layout.edges.length} connections`;
  const t10 = "block w-full cursor-grab [touch-action:pan-y] active:cursor-grabbing max-md:h-auto";
  const t11 = layout.height;
  const t12 = `0 0 ${graphWidth} ${layout.height}`;
  let t13;
  if ($[7] !== hoveredNodeId || $[8] !== onSelect || $[9] !== previewedEdgeIndex || $[10] !== selection) {
    t13 = edge_0 => {
      const selected = selection.kind === "edge" && selection.index === edge_0.index;
      const previewed = previewedEdgeIndex === edge_0.index;
      const active = selected || previewed;
      const isConnectedToHoveredNode = !hoveredNodeId || edge_0.source.id === hoveredNodeId || edge_0.target.id === hoveredNodeId;
      const selectEdge = () => onSelect({
        kind: "edge",
        index: edge_0.index
      });
      return <g aria-label={getEdgeAriaLabel(edge_0)} className="cursor-pointer outline-none" key={`${edge_0.source.id}-${edge_0.target.id}-${edge_0.index}`} onBlur={() => setFocusedEdgeIndex(null)} onClick={selectEdge} onFocus={() => setFocusedEdgeIndex(edge_0.index)} onKeyDown={event_1 => handleSelectKey(event_1, selectEdge)} onMouseEnter={() => setPointerEdgeIndex(edge_0.index)} onMouseLeave={() => setPointerEdgeIndex(null)} opacity={isConnectedToHoveredNode ? 1 : 0.12} role="button" tabIndex={0}><path d={edge_0.path} fill="none" stroke={active ? "var(--border-color-accent)" : "var(--text-color-secondary)"} strokeLinecap="round" strokeWidth={active ? 2.6 : 1.5} /><path d={edge_0.path} fill="none" pointerEvents="stroke" stroke="transparent" strokeWidth="18" /></g>;
    };
    $[7] = hoveredNodeId;
    $[8] = onSelect;
    $[9] = previewedEdgeIndex;
    $[10] = selection;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const t14 = layout.edges.map(t13);
  const t15 = layout.nodes.map(node => {
    const presentation = nodePresentation[node.kind];
    const selected_0 = selection.kind === "node" && selection.id === node.id;
    const endpointSelected = selectedEdgeEndpoints.has(node.id);
    const isDimmed = hoveredNodeId !== null && !hoveredNeighborhood.has(node.id);
    const label = truncate(node.id, nodeLabelLength);
    const isNeighborOfHovered = hoveredNodeId !== null && hoveredNeighborhood.has(node.id);
    const showLabel = !dense || node.kind !== "other" || selected_0 || endpointSelected || isNeighborOfHovered;
    const selectNode = () => onSelect({
      kind: "node",
      id: node.id
    });
    return <g aria-label={`Inspect ${presentation.kind}: ${node.id}`} className="cursor-pointer outline-none" key={node.id} onClick={selectNode} onKeyDown={event_2 => handleSelectKey(event_2, selectNode)} onMouseEnter={() => setHoveredNodeId(node.id)} onMouseLeave={() => setHoveredNodeId(null)} opacity={isDimmed ? 0.18 : 1} role="button" tabIndex={0} transform={`translate(${node.x},${node.y})`}><title>{node.id}</title>{(selected_0 || endpointSelected) && <circle fill="none" r={nodeRadius + 5} stroke="var(--border-color-accent)" strokeDasharray={selected_0 ? undefined : "3 3"} strokeWidth={selected_0 ? 2 : 1} />}<circle fill={presentation.fill} r={nodeRadius} stroke={presentation.stroke} strokeWidth={node.kind === "other" ? 1.5 : 2} /><text dominantBaseline="central" fill={presentation.stroke} fontFamily="ui-monospace, monospace" fontSize={nodeGlyphFontSize} fontWeight="700" pointerEvents="none" textAnchor="middle">{presentation.glyph}</text>{showLabel && <text fill="var(--text-color-primary)" fontSize={nodeLabelFontSize} fontWeight={selected_0 || endpointSelected ? "600" : "500"} pointerEvents="none" textAnchor="middle" y={nodeRadius + 13}>{label}</text>}</g>;
  });
  let t16;
  if ($[12] !== t14 || $[13] !== t15) {
    t16 = <g ref={viewportRef}>{t14}{t15}</g>;
    $[12] = t14;
    $[13] = t15;
    $[14] = t16;
  } else {
    t16 = $[14];
  }
  let t17;
  if ($[15] !== layout.height || $[16] !== t12 || $[17] !== t16 || $[18] !== t9) {
    t17 = <svg ref={svgRef} aria-label={t9} className={t10} height={t11} viewBox={t12}>{t16}</svg>;
    $[15] = layout.height;
    $[16] = t12;
    $[17] = t16;
    $[18] = t9;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="hidden md:inline"> · drag to pan · ⌘/Ctrl-scroll to zoom</span>;
    t19 = <span className="md:hidden"> · pinch to zoom</span>;
    $[20] = t18;
    $[21] = t19;
  } else {
    t18 = $[20];
    t19 = $[21];
  }
  let t20;
  if ($[22] !== layout.edges.length || $[23] !== layout.nodes.length) {
    t20 = <div className="pointer-events-none absolute bottom-3 left-3 rounded-md bg-primary/90 px-2 py-1 text-caption-4 text-tertiary">{layout.nodes.length} entities · {layout.edges.length} connections{t18}{t19}</div>;
    $[22] = layout.edges.length;
    $[23] = layout.nodes.length;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t17 || $[26] !== t20 || $[27] !== t7 || $[28] !== t8) {
    t21 = <div className={t6}>{t7}{t8}{t17}{t20}</div>;
    $[25] = t17;
    $[26] = t20;
    $[27] = t7;
    $[28] = t8;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  return t21;
};
type EdgeInspectorProps = {
  edge: ClusterEdge;
};
const EdgeInspector = t0 => {
  const $ = _c(35);
  const {
    edge
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-caption-3 text-tertiary">Selected connection</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== edge.source) {
    t2 = <InlineText text={edge.source} />;
    $[1] = edge.source;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-tertiary">↔</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== edge.target) {
    t4 = <InlineText text={edge.target} />;
    $[4] = edge.target;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="mt-1 text-label-2 text-primary">{t2} {t3} {t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <dt className="text-caption-3 text-tertiary">Attribute</dt>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== edge.attribute) {
    t7 = <div className="min-w-0">{t6}<dd className="mt-1 break-words text-label-2 text-primary"><InlineText text={edge.attribute} /></dd></div>;
    $[10] = edge.attribute;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <dt className="text-caption-3 text-tertiary">Matched value</dt>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== edge.value) {
    t9 = edge.value ? <InlineText text={edge.value} /> : "\u2014";
    $[13] = edge.value;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <div className="min-w-0">{t8}<dd className="mt-1 break-words text-label-2 text-primary">{t9}</dd></div>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <dt className="text-caption-3 text-tertiary">Match detail</dt>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== edge.detail) {
    t12 = edge.detail ? <InlineText text={edge.detail} /> : "\u2014";
    $[18] = edge.detail;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12) {
    t13 = <div className="min-w-0">{t11}<dd className="mt-1 break-words text-body-2 text-secondary">{t12}</dd></div>;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t13 || $[24] !== t7) {
    t14 = <dl className="mt-4 grid gap-3 border-t border-tertiary pt-4 sm:grid-cols-3">{t7}{t10}{t13}</dl>;
    $[22] = t10;
    $[23] = t13;
    $[24] = t7;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="text-label-3">Evidence</span>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== edge.evidence) {
    t16 = edge.evidence.map(_temp);
    $[27] = edge.evidence;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16) {
    t17 = <div className="mt-3 flex flex-wrap items-center gap-1.5 text-caption-4 text-tertiary">{t15}{t16}</div>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t14 || $[32] !== t17 || $[33] !== t5) {
    t18 = <aside aria-label="Selected connection" className="rounded-lg border border-tertiary bg-secondary p-4">{t1}{t5}{t14}{t17}</aside>;
    $[31] = t14;
    $[32] = t17;
    $[33] = t5;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
};
type NodeInspectorProps = {
  node: ClusterGraphNode;
};
const NodeInspector = t0 => {
  const $ = _c(19);
  const {
    node
  } = t0;
  const presentation = nodePresentation[node.kind];
  let t1;
  if ($[0] !== node) {
    t1 = getNodeHref(node);
    $[0] = node;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const href = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-caption-3 text-tertiary">Selected entity</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = node.kind === "other" ? "mt-1 break-words text-label-2 text-primary" : "mt-1 break-all text-snippet-3 text-primary";
  let t4;
  if ($[3] !== node.id) {
    t4 = <InlineText text={node.id} />;
    $[3] = node.id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className="min-w-0">{t2}<div className={t3}>{t4}</div></div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== presentation.kind) {
    t6 = <span className="rounded-full border border-primary bg-primary px-2.5 py-1 text-caption-4 text-secondary">{presentation.kind}</span>;
    $[8] = presentation.kind;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="flex flex-wrap items-start justify-between gap-3">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== href || $[14] !== node.id) {
    t8 = href && <Link className="mt-3 inline-flex items-center gap-1 text-label-3 text-accent hover:underline" data-footprint-id={node.id} href={href}>Open entity<IcoArrowTopRight16 aria-hidden="true" /></Link>;
    $[13] = href;
    $[14] = node.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <aside aria-label="Selected entity" className="rounded-lg border border-tertiary bg-secondary p-4">{t7}{t8}</aside>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
const ArtifactClusterBlock = t0 => {
  const $ = _c(28);
  const {
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "edge",
      index: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selection, setSelection] = useState(t1);
  let t2;
  if ($[1] !== config.edges) {
    const entities = getClusterEntities(config.edges);
    t2 = new Map(entities.map(_temp2));
    $[1] = config.edges;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const entityById = t2;
  const selectedEdge = selection.kind === "edge" ? config.edges[selection.index] : undefined;
  let t3;
  if ($[3] !== entityById || $[4] !== selection.id || $[5] !== selection.kind) {
    t3 = selection.kind === "node" ? entityById.get(selection.id) : undefined;
    $[3] = entityById;
    $[4] = selection.id;
    $[5] = selection.kind;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const selectedNode = t3;
  const t4 = config.title ?? "Entity cluster";
  let t5;
  if ($[7] !== config.description || $[8] !== config.title) {
    t5 = (config.title || config.description) && <header className="flex items-start gap-3 border-b border-tertiary px-4 py-4 md:px-6 md:py-5"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-tertiary bg-secondary text-secondary"><IcoNetwork16 aria-hidden="true" /></span><div className="min-w-0">{config.title && <h2 className="break-words text-label-2 text-primary"><InlineText text={config.title} /></h2>}{config.description && <div className="mt-1 break-words text-body-3 text-tertiary"><InlineText text={config.description} /></div>}</div></header>;
    $[7] = config.description;
    $[8] = config.title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== config.edges || $[11] !== selection) {
    t6 = <ClusterGraph edges={config.edges} selection={selection} onSelect={setSelection} />;
    $[10] = config.edges;
    $[11] = selection;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== selectedEdge) {
    t7 = selectedEdge && <EdgeInspector edge={selectedEdge} />;
    $[13] = selectedEdge;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== selectedNode) {
    t8 = selectedNode && <NodeInspector node={selectedNode} />;
    $[15] = selectedNode;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className="grid gap-3 p-3 md:p-4">{t6}{t7}{t8}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== config.note) {
    t10 = config.note && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><InlineText text={config.note} /></div>;
    $[21] = config.note;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t4 || $[25] !== t5 || $[26] !== t9) {
    t11 = <section aria-label={t4} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t5}{t9}{t10}</section>;
    $[23] = t10;
    $[24] = t4;
    $[25] = t5;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export default ArtifactClusterBlock;
function _temp(evidence, index) {
  return <span key={index}><InlineText text={evidence} /></span>;
}
function _temp2(entity) {
  return [entity.id, entity];
}